/**
 * クリップボード自動クリア — CLAUDE.md §2 非交渉ルール 7 (90 秒以内に必ずクリア)。
 *
 * 設計判断:
 *   - 既定 TTL は 90 秒。秘匿情報 (パスワード / TOTP / リカバリキー) のコピー時に必ず使う
 *   - Track D: ユーザーが `settings.preferences.clipboardTtlSec` で 5-600 秒の
 *     範囲で上書き可能。caller が `ttlSec` を明示しない場合は preferences →
 *     CLIPBOARD_DEFAULT_TTL_SEC の順で resolve する
 *   - 二重コピー時は前回タイマを取消してから新しい値を書く + 新タイマを起動
 *   - タブが非アクティブになっても (visibilitychange / blur) タイマは止めない
 *     → 攻撃者がタブ切替で TTL を伸ばせない
 *   - タブクローズ時は OS / ブラウザ任せ。仕様上 timer は破棄されるが、これは
 *     クリップボード値も含めて OS 側で揮発させる方針 (chromium / safari は
 *     copy 元のページが閉じてもクリップボードは保持されるため、ここでは
 *     ベストエフォートの保証のみとする)
 *   - `navigator.clipboard.writeText` がない / 拒否される環境向けに `<textarea> +
 *     execCommand('copy')` のフォールバックを提供
 *   - DI を許す形 (deps 引数) でテスト容易性を確保
 */
import { preferencesStore } from '@features/preferences/services/preferencesStore'

export const CLIPBOARD_DEFAULT_TTL_SEC = 90

/**
 * Toast 等の UI 表示で「{ttl} 秒後にクリア」を出すときに使う実効 TTL。
 * preferences で上書きされていればそちら、未設定なら既定 90 を返す。
 */
export const getEffectiveClipboardTtlSec = (): number =>
  preferencesStore.getResolved().clipboardTtlSec

export interface CopyToClipboardOptions {
  /** 自動クリアまでの秒数。既定 90 (CLAUDE.md §2 ルール 7)。 */
  ttlSec?: number
}

export interface ClipboardDeps {
  writeText: (value: string) => Promise<void>
  setTimeout: (fn: () => void, ms: number) => ReturnType<typeof globalThis.setTimeout>
  clearTimeout: (id: ReturnType<typeof globalThis.setTimeout>) => void
}

const browserDeps = (): ClipboardDeps => {
  const writeViaApi = async (value: string): Promise<void> => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)

      return
    }

    // フォールバック: hidden textarea + document.execCommand('copy')
    if (typeof document === 'undefined') {
      throw new Error('clipboard: no DOM')
    }
    const ta = document.createElement('textarea')

    ta.value = value
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    ta.style.pointerEvents = 'none'
    document.body.appendChild(ta)
    ta.select()
    try {
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- legacy fallback only
      document.execCommand('copy')
    } finally {
      document.body.removeChild(ta)
    }
  }

  return {
    writeText: writeViaApi,
    setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
    clearTimeout: id => globalThis.clearTimeout(id),
  }
}

let pendingTimerId: ReturnType<typeof globalThis.setTimeout> | null = null
let activeDeps: ClipboardDeps | null = null

const cancelPending = (): void => {
  if (pendingTimerId !== null && activeDeps !== null) {
    activeDeps.clearTimeout(pendingTimerId)
    pendingTimerId = null
  }
}

/**
 * `text` をクリップボードに書き込み、`ttlSec` (既定 90) 秒後に空文字で上書きする。
 *
 * 二重呼出時: 既存タイマを取消し → 新しい値を書込 → 新タイマ起動 (TTL は再カウント)。
 */
export const copyToClipboard = async (
  text: string,
  options: CopyToClipboardOptions = {},
  deps: ClipboardDeps = browserDeps(),
): Promise<void> => {
  // 優先順位: 呼出側で options.ttlSec 明示 > ユーザ preferences > CLAUDE.md §2 既定 90s
  const ttlSec = options.ttlSec ?? preferencesStore.getResolved().clipboardTtlSec

  cancelPending()
  activeDeps = deps

  await deps.writeText(text)

  pendingTimerId = deps.setTimeout(() => {
    pendingTimerId = null
    void deps.writeText('').catch(() => {
      // クリア失敗は致命ではない (権限拒否 / DOM 破棄 等)。次回コピーで上書きされる
    })
  }, ttlSec * 1000)
}

/**
 * 待機中の自動クリアを取り消し、モジュール状態をリセットする。
 *
 * プロダクション用途: タブ離脱時のクリーンアップ等 (クリップボード値はそのまま残る)。
 * テスト用途: `beforeEach` / `afterEach` で呼ぶことでシングルトン状態を完全リセットできる
 * (`pendingTimerId = null` + `activeDeps = null`)。
 */
export const cancelPendingClear = (): void => {
  cancelPending()
  activeDeps = null
}
